import { Dispatch, FC, SetStateAction, useCallback, useMemo } from 'react'
import { type Swiper as SwiperType } from 'swiper'
import { FreeMode, Mousewheel } from 'swiper/modules'
import { Swiper as SwiperComponent, SwiperSlide } from 'swiper/react'

import { cn } from '@/app/utils'
import { AppCheckboxBadged } from '@/components/shared/checkbox'
import { useCheckboxListSwiper } from '@/components/shared/checkbox/hooks/useCheckboxListSwiper'
import {
  checkboxItemAll,
  checkboxItemUnread
} from '@/components/shared/checkbox/lib/constants'
import {
  getModifiedCheckboxList,
  isCheckboxItemChecked
} from '@/components/shared/checkbox/lib/utils'

import { notificationTypeOptionsMap } from './NotificationTypesFilter.constants'
import { NotificationTypeValue } from './NotificationTypesFilter.types'
import { NotificationUnreadFilter } from './NotificationUnreadFilter'

type Props = {
  selectedTypes?: string[]
  className?: string
  onFilterChange: Dispatch<SetStateAction<string[] | undefined>>
  unreadCount?: number
  isUnreadCountLoading?: boolean
}

const NotificationTypesFilter: FC<Props> = ({
  selectedTypes,
  className,
  onFilterChange,
  unreadCount,
  isUnreadCountLoading
}) => {
  const { swiperRef, showSwiper } = useCheckboxListSwiper({
    selectedItems: selectedTypes
  })

  const BadgeWrapper = showSwiper ? SwiperSlide : 'div'
  const badgeWrapperClass = showSwiper ? '!w-auto' : ''

  const notificationTypeOptions = useMemo(
    () =>
      notificationTypeOptionsMap.map(option => ({
        ...option,
        checked: isCheckboxItemChecked(option.value, selectedTypes)
      })),
    [selectedTypes]
  )

  const handleCheckboxChange = useCallback(
    (
      target: { value: string; checked: boolean },
      optionValue: NotificationTypeValue | NotificationTypeValue[]
    ) => {
      onFilterChange(prev =>
        getModifiedCheckboxList(
          {
            value: optionValue,
            checked: target.checked
          },
          notificationTypeOptionsMap,
          prev
        )
      )
    },
    [onFilterChange]
  )

  const notificationTypeBadges = useMemo(
    () =>
      notificationTypeOptions.map(option => (
        <BadgeWrapper
          key={Array.isArray(option.value) ? option.value[0] : option.value}
          className={badgeWrapperClass}
        >
          <AppCheckboxBadged
            {...option}
            closable={option.value !== checkboxItemAll}
            onChange={({ target }) =>
              handleCheckboxChange(target, option.value)
            }
            className={option.value === checkboxItemUnread ? 'pr-4' : ''}
            extraContent={
              option.value === checkboxItemUnread && (
                <NotificationUnreadFilter
                  unreadCount={unreadCount}
                  isLoading={isUnreadCountLoading}
                />
              )
            }
          />
        </BadgeWrapper>
      )),
    [
      BadgeWrapper,
      badgeWrapperClass,
      handleCheckboxChange,
      notificationTypeOptions,
      unreadCount,
      isUnreadCountLoading
    ]
  )

  if (!showSwiper) {
    return (
      <div className={cn('flex flex-wrap gap-8', className)}>
        {notificationTypeBadges}
      </div>
    )
  }

  return (
    <SwiperComponent
      modules={[FreeMode, Mousewheel]}
      slidesPerView="auto"
      spaceBetween={8}
      mousewheel
      watchOverflow
      threshold={10}
      className={className}
      onInit={(swiper: SwiperType) => (swiperRef.current = swiper)}
    >
      {notificationTypeBadges}
    </SwiperComponent>
  )
}

export default NotificationTypesFilter
